import {faArrowsLeftRight} from "@fortawesome/pro-solid-svg-icons/faArrowsLeftRight";
import {faArrowsUpDown} from "@fortawesome/pro-solid-svg-icons/faArrowsUpDown";
import {
    faArrowUpRightAndArrowDownLeftFromCenter
} from "@fortawesome/pro-solid-svg-icons/faArrowUpRightAndArrowDownLeftFromCenter";
import {faTruckFast} from "@fortawesome/pro-solid-svg-icons/faTruckFast";
import {faWeightScale} from "@fortawesome/pro-solid-svg-icons/faWeightScale";
import {FontAwesomeIcon} from "@fortawesome/react-fontawesome";
import {getShippingOptions, useInfiniteShippingCostPresets} from "@twigsby/client-data";
import {
    Button,
    ButtonSize,
    ButtonSubmit,
    ColourSolid,
    ComboBoxControl,
    ContentBlock,
    ContentLoader,
    CountryCodeControl,
    FlexAlign,
    FlexGap,
    FlexJustify,
    FormWrapperLabel,
    ItemRow,
    ItemRowHeader,
    SelectItemType,
    StackVertical,
    useLayout,
    useTranslation
} from "@twigsby/frontend-framework";
import {Controller, FormProvider, useForm} from "@twigsby/frontend-framework/lib/ReactHookForm";
import {FormatCurrency} from "@twigsby/helpers";
import React, {useEffect, useState} from "react";
import {useParams} from "react-router-dom";
import {useSite} from "../../Providers/SiteInterfaceProvider";

const defaultShipping = {
    weight: 0.0,
    length: 0.0,
    depth: 0.0,
    width: 0.0,
    countryCode: 'GB',
};

interface ShippingItemType {
    depth: number;
    height: number;
    weight: number;
    width: number;
}

interface ShippingPresetItemType extends ShippingItemType {
    mailItemSizeDescription: string;
    mailItemSizeId: number;
    mailTypeId:number;
}

type ShippingOptionType = {
    description: string;
    durationDescription: string;
    minDaysToDeliver: number;
    maxDaysToDeliver: number;
    cost: number;
    costWithTax: number;
}

const ShippingCalculator = () => {
    const params = useParams();

    const { t } = useTranslation();
    const { isMedium } = useLayout();
    const { currentLocale, updatePageMeta } = useSite();

    const formMethods = useForm({
        defaultValues: defaultShipping,
        mode: "onChange"
    });

    const [ formValues, setFormValues ] = useState<any>(null);
    const [ shippingValues, setShippingValues ] = useState<any>(null);

    const [ updateShipping, setUpdateShipping ] = useState<boolean>(false);

    const { data: presetsData, status: presetsStatus, error: presetsError } = useInfiniteShippingCostPresets();

    const [ shapedData, setShapedData] = useState<Array<SelectItemType>>([]);

    const [ weightUnit, setWeightUnit] = useState<string>('G');
    const [ weightStep, setWeightStep] = useState<number>(1);
    const [ measurementUnit, setMeasurementUnit] = useState<string>('CM');
    const [ measurementStep, setMeasurementStep] = useState<number>(0.01);

    const CheckAndShapeData = (weight: number, width: number, length: number, depth: number, fromPreset?: boolean) => {
        return {
            weight: weightUnit === 'KG' ? ConvertGramsToKilo(weight, fromPreset ?? false) : weight,
            width: measurementUnit === 'IN' ? ConvertCmToInches(width, fromPreset ?? false) : width,
            length: measurementUnit === 'IN'? ConvertCmToInches(length, fromPreset ?? false) : length,
            depth: measurementUnit === 'IN' ? ConvertCmToInches(depth, fromPreset ?? false) : depth,
            submitWeight: weightUnit === 'KG' ? ConvertGramsToKilo(weight, true) : weight,
            submitWidth: measurementUnit === 'IN' ? ConvertCmToInches(width, true) : width,
            submitLength: measurementUnit === 'IN'? ConvertCmToInches(length, true) : length,
            submitDepth: measurementUnit === 'IN' ? ConvertCmToInches(depth, true) : depth,
            countryCode: formMethods.getValues().countryCode,
        };
    }

    const SetPresetData = (option: number, fromPreset?: boolean) => {
        const item : ShippingPresetItemType = presetsData?.pages.map((page: any) =>
            page.data.filter((item: ShippingPresetItemType) => item.mailItemSizeDescription === shapedData[option].label)[0]
        )[0];

        if(item) {
            UpdatePackageValues(item, fromPreset);
        }
    }

    const UpdatePackageValues = (params: Record<string, any>, fromPreset?: boolean) => {
        const newParams = Object.keys(params).length > 0 ? params : {
            weight: 0,
            width: 0,
            height: 0,
            depth: 0
        };

        const parsedData = CheckAndShapeData(newParams.weight, newParams.width, newParams.height, newParams.depth, fromPreset);

        formMethods.setValue('weight', parsedData.submitWeight);
        formMethods.setValue('width', parsedData.submitWidth);
        formMethods.setValue('length', parsedData.submitLength);
        formMethods.setValue('depth', parsedData.submitDepth);
        formMethods.trigger();

        if(Object.keys(params).length > 0) {
            setFormValues({});
            setUpdateShipping(!updateShipping);
        } else {
            setShippingValues(null);
        }
    }

    const ShapePresetsData = () => {
        let selectOptions: Array<SelectItemType> = [];

        if(!presetsData) return selectOptions;

        presetsData.pages.map((page: any) =>
            page.data.map((item: ShippingPresetItemType, index: number) => selectOptions.push({label: item.mailItemSizeDescription, value: index}))
        )

        return selectOptions;
    }

    const ConvertGramsToKilo = (value: number, reverse: boolean) => {
        return parseFloat((reverse ? value / 1000 : value * 1000).toFixed(4));
    }

    const ConvertCmToInches = (value: number, reverse: boolean) => {
        return parseFloat((reverse ? value / 2.54 : value * 2.54).toFixed(2));
    }

    const ConvertWeight = () => {
        setWeightStep(measurementUnit !== 'G' ? 0.0001 : 1);
        const newWeight = ConvertGramsToKilo(formMethods.getValues('weight'), weightUnit !== 'G');
        formMethods.setValue('weight', newWeight);

        formMethods.trigger();
    }

    const ConvertMeasurements = () => {
        setMeasurementStep(measurementUnit !== 'CM' ? 0.01 : 0.01);
        const newWidth = ConvertCmToInches(formMethods.getValues('width'), measurementUnit !== 'CM');
        const newLength = ConvertCmToInches(formMethods.getValues('length'), measurementUnit !== 'CM');
        const newDepth = ConvertCmToInches(formMethods.getValues('depth'), measurementUnit !== 'CM');

        formMethods.setValue('width', newWidth);
        formMethods.setValue('length', newLength);
        formMethods.setValue('depth', newDepth);

        formMethods.trigger();
    }

    const HandleSend = (data: any) => {
        setShippingValues(CheckAndShapeData(data.weight, data.width, data.length, data.depth));
        setUpdateShipping(!updateShipping);
    }

    useEffect(() => {
        ConvertWeight();
    }, [weightUnit]);

    useEffect(() => {
        ConvertMeasurements();
    }, [measurementUnit]);

    useEffect(() => {
        if(presetsData) {
            setShapedData(ShapePresetsData());
        }
    }, [presetsData]);

    useEffect(() => {
        if(params) {
            UpdatePackageValues(params);
        }
    }, [params]);

    useEffect(() => {
        updatePageMeta({
            title: t('shippingCalculator.title')
        });
    }, [currentLocale]);

    return (
        <div className='content-main content-area-narrow '>
            <div className={'container-narrow pt-lg '}>

                <h1>{t('shippingCalculator.title')}</h1>

                <div className={'row mb-lg'}>
                    <div className={'col-xs-12 col-lg-6 col-xl-5'}>

                        <FormProvider {...formMethods}>
                            <form onSubmit={formMethods.handleSubmit((data, e) => HandleSend(data))} className={'sticky'}>
                                <fieldset>
                                    <h5 className={'mb-0'}>{t('shippingCalculator.labels.destination')}</h5>
                                    <p>{t('shippingCalculator.labels.destinationDescription')}</p>

                                    <div className={'mb-xl'}>
                                        <Controller
                                            control={formMethods.control}
                                            name="countryCode"
                                            rules={{ required: true }}
                                            render={({field}) =>
                                                <FormWrapperLabel label={t('shippingCalculator.labels.countryCode')}>
                                                    <CountryCodeControl
                                                        placeholder={t('shippingCalculator.controls.countryCodePlaceholder')}
                                                        name={'countryCode'}
                                                        singleSelection={true}
                                                        defaultValues={[field.value]}
                                                        forwardValueCallback={(val) => field.onChange(val)}
                                                    />
                                                </FormWrapperLabel>
                                            }
                                        />
                                    </div>

                                    <h5 className={'mb-0'}>{t('shippingCalculator.labels.itemSize')}</h5>
                                    <p>{t('shippingCalculator.labels.itemSizeDescription')}</p>

                                    <div className={'flex-column flex-gap-md mb-md'}>
                                        {presetsStatus === "error" ? (
                                            <ContentBlock error={true} title={presetsError.name} content={presetsError.message} />
                                        ) : (
                                            <FormWrapperLabel label={t('shippingCalculator.labels.itemPresets')}>
                                                <ComboBoxControl
                                                    name={'shippingPresets'}
                                                    placeholder={t('shippingCalculator.controls.itemPresetsPlaceholder')}
                                                    valueType={'number'}
                                                    data={shapedData}
                                                    loading={shapedData === null || presetsStatus === "loading" || presetsError !== null}
                                                    forwardValueCallback={(e) => SetPresetData(e, true)}
                                                    singleSelection={true}
                                                />
                                            </FormWrapperLabel>
                                        )}

                                        <div className={`${isMedium ? 'flex-row' : 'flex-column'} flex-gap-sm`}>
                                            <FormWrapperLabel
                                                label={t('shippingCalculator.labels.weight')}
                                                invalid={formMethods.formState.errors.weight && formMethods.formState.errors.weight?.message !== ''}
                                                validationMessage={formMethods.formState.errors.weight?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={weightUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faWeightScale} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step={weightStep} aria-invalid={formMethods.formState.errors.weight ? 'true' : 'false'} {...formMethods.register('weight', { required: "This is a required field", min: 0.0001 }) } />
                                                </div>
                                            </FormWrapperLabel>

                                            <div className={'form-group'}>
                                                <label>{weightUnit === 'G' ? t('units.grams') : t('units.kilograms')}</label>
                                                <div className={'btn-group btn-group-row mb-0'}>
                                                    <Button title={t('units.gramsShort')} onClick={() => setWeightUnit('G')} colour={weightUnit === 'G' ? ColourSolid.Selected : ColourSolid.Secondary} />
                                                    <Button title={t('units.kilogramsShort')} onClick={() => setWeightUnit('KG')} colour={weightUnit === 'KG' ? ColourSolid.Selected : ColourSolid.Secondary} />
                                                </div>
                                            </div>
                                        </div>

                                        <div className={`${isMedium ? 'flex-row' : 'flex-column'} flex-gap-sm`}>
                                            <FormWrapperLabel
                                                label={t("shippingCalculator.labels.width")}
                                                invalid={formMethods.formState.errors.width && formMethods.formState.errors.width?.message !== ''}
                                                validationMessage={formMethods.formState.errors.width?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={measurementUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faArrowsLeftRight} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step={measurementStep} aria-invalid={formMethods.formState.errors.width ? 'true' : 'false'} {...formMethods.register('width', { required: "This is a required field", min: 0.1 })} />
                                                </div>
                                            </FormWrapperLabel>

                                            <FormWrapperLabel
                                                label={t("shippingCalculator.labels.height")}
                                                invalid={formMethods.formState.errors.depth && formMethods.formState.errors.depth?.message !== ''}
                                                validationMessage={formMethods.formState.errors.depth?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={measurementUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faArrowsUpDown} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step={measurementStep} aria-invalid={formMethods.formState.errors.depth ? 'true' : 'false'} {...formMethods.register('depth', { required: "This is a required field", min: 0.1  })} />
                                                </div>
                                            </FormWrapperLabel>

                                            <FormWrapperLabel
                                                label={t("shippingCalculator.labels.depth")}
                                                invalid={formMethods.formState.errors.length && formMethods.formState.errors.length?.message !== ''}
                                                validationMessage={formMethods.formState.errors.length?.message as string}
                                            >
                                                <div className={'input-addon addon-first'}>
                                                    <div title={measurementUnit} className={'input-addon-icon'}>
                                                        <FontAwesomeIcon icon={faArrowUpRightAndArrowDownLeftFromCenter} fixedWidth={true} />
                                                    </div>
                                                    <input type='number' step={measurementStep} aria-invalid={formMethods.formState.errors.length ? 'true' : 'false'} {...formMethods.register('length', { required: "This is a required field", min: 0.1  })} />
                                                </div>
                                            </FormWrapperLabel>

                                            <div className={'form-group'}>
                                                <label>{measurementUnit === 'CM' ? t("units.centimeter") : t("units.inches")}</label>
                                                <div className={'btn-group btn-group-row mb-0'}>
                                                    <Button title={t("units.centimeterShort")} onClick={() => setMeasurementUnit('CM')} colour={measurementUnit === 'CM' ? ColourSolid.Selected : ColourSolid.Secondary} />
                                                    <Button title={t("units.inchesShort")} onClick={() => setMeasurementUnit('IN')} colour={measurementUnit === 'IN' ? ColourSolid.Selected : ColourSolid.Secondary} />
                                                </div>
                                            </div>
                                        </div>

                                    </div>

                                    <ButtonSubmit title={updateShipping ? t("shippingCalculator.actions.updateRates") : t("shippingCalculator.actions.getRates")} disabled={!formMethods.formState.isValid} fullWidth={true} colour={ColourSolid.Primary} />

                                </fieldset>
                            </form>
                        </FormProvider>
                    </div>

                    <div className={'col-xs-12 col-lg-6 col-xl-7'}>
                        <>
                            {shippingValues && formMethods.formState.isValid ? (
                                <ShippingOptions
                                    updateValues={updateShipping}
                                    width={shippingValues.width}
                                    depth={shippingValues.depth}
                                    length={shippingValues.length}
                                    weight={shippingValues.weight}
                                    countryCode={shippingValues.countryCode}
                                />
                            ) : (
                                <ContentBlock
                                    stacked={true}
                                    iconContent={{icon: faTruckFast}}
                                    title={t("shippingCalculator.noResults.title")}
                                    content={t("shippingCalculator.noResults.description")}
                                    showBorder={true}
                                />
                            )}
                        </>
                    </div>
                </div>

            </div>
        </div>
    )
}

export default ShippingCalculator

type ShippingOptionsType = {
    updateValues: boolean;
    countryCode: string;
    width: number;
    length: number;
    depth: number;
    weight: number;
}

const ShippingOptions = (props: ShippingOptionsType) => {
    const { t } = useTranslation();
    const [shippingOptionsLoading, setShippingOptionsLoading] = useState<boolean>(false);
    const [shippingOptions, setShippingOptions] = useState<any>(null);
    const [groupData, setGroupData] = useState<any>(null);
    const {UpdateSiteContact} = useSite();

    const FetchOptions = async () => {
        setShippingOptionsLoading(true);
        await getShippingOptions(props.countryCode, props.width, props.length, props.depth, props.weight).then((r) => {
            setShippingOptions(r);
        }).finally(() => setShippingOptionsLoading(false));
    }

    const MapCourierLogo = (props: any) => {
        if(props.value.indexOf('DPD') > -1) {
            return <img src={`${process.env.PUBLIC_URL}/images/dpd-logo.svg`} alt={`DPD ${t("logo")}`} style={{ maxWidth : 'calc(var(--xl-unit) * 2)'}} />
        } else if(props.value.indexOf('DHL') > -1) {
            return <img src={`${process.env.PUBLIC_URL}/images/dhl-logo.svg`} alt={`DHL ${t("logo")}`} style={{ maxWidth : 'calc(var(--xl-unit) * 2)'}} />
        } else if(props.value.indexOf('Parcel') > -1) {
            return <img src={`${process.env.PUBLIC_URL}/images/parcel-force-logo.png`} alt={`Parcelforce ${t("logo")}`} style={{ maxWidth : 'calc(var(--xl-unit) * 2)'}} />
        }

        return <img src={`${process.env.PUBLIC_URL}/images/royal-mail-logo.svg`} alt={`Royal Mail ${t("logo")}`} style={{ maxWidth : 'calc(var(--xl-unit) * 2)' }} />
    }

    const GroupData = (data: any) => {
        const groups = data.reduce((groups: any, item: any) => {
            const title = item.description.split(' ')[0];

            if (!groups[title]) {
                groups[title] = [];
            }

            groups[title].push(item);

            return groups;
        }, {});

        setGroupData(groups);
    }

    const HandleShippingQuoteBuilder = () => {
        UpdateSiteContact(
            true,
            1,
            `Requesting a shipping cost quote for:\n\nDestination Country:  ${props.countryCode} \nWidth:  ${props.width} \nLength:  ${props.length} \nDepth:  ${props.depth} \nWeight:  ${props.weight}`
        );
    }

    const RenderGroup = (data: any) => {
        let groups = [];

        for (const [key, value] of Object.entries(data) as any) {
            groups.push(
                <div className={'card mb-xl pb-xs shipping-options-rows'} key={`shippingGroup${Math.floor(Math.random() * (20000 - 19000 + 1) + 19000)}`}>
                    <div className={'flex-column sticky'}>
                        <div className={'flex-row p-md glass'}>
                            <MapCourierLogo value={key} />
                            <h5 className={'mb-0 ml-md'}>{key === 'Royal' ? 'Royal Mail' : key}</h5>
                        </div>
                        <ItemRowHeader>
                            <div className={'pl-md'}>{t("shippingCalculator.headings.description")}</div>
                            <div>{t("shippingCalculator.headings.deliveryTime")}</div>
                            <div className={'text-right'}>{t("shippingCalculator.headings.cost")}</div>
                            <div className={'text-right pr-md'}>{t("shippingCalculator.headings.vat")}</div>
                        </ItemRowHeader>
                    </div>

                    {value.map((item: ShippingOptionType, index: number) =>
                        <ItemRow
                            key={`shippingOption${index}`}
                            static={true}
                        >
                            <div className={'pl-md'}>{item.description}</div>
                            <div>{item.durationDescription}</div>
                            {item.cost > 0 ? (
                                <>
                                    <div className={'text-right'}>{FormatCurrency(item.cost)}</div>
                                    <div className={'text-right pr-md'}>{FormatCurrency(item.costWithTax)}</div>
                                </>
                            ) : (
                                <div style={{ gridColumn: 'span 2', paddingInlineEnd: 'var(--sm-unit)' }}>
                                    <Button
                                        onClick={() => HandleShippingQuoteBuilder()}
                                        title={'Contact Us'}
                                        colour={ColourSolid.Primary}
                                        fullWidth={true}
                                    />
                                </div>
                            )}
                        </ItemRow>
                    )}
                </div>
            )
        }

        return (
            <>
                {groups}
            </>
        )
    }

    useEffect(() => {
        if(shippingOptions?.data) {
            GroupData(shippingOptions.data);
        }
    }, [shippingOptions]);

    useEffect(() => {
        FetchOptions().then(() => null);
    }, [props.updateValues]);

    if(!groupData) return null;

    return (
        <>
            {(!groupData || shippingOptionsLoading) && (
                <ContentLoader />
            )}

            {groupData && !shippingOptionsLoading && Object.keys(groupData).length === 0 && (
                <StackVertical gap={FlexGap.ExtraSmall} align={FlexAlign.Center} justify={FlexJustify.Center} extraClass={'no-content-border m-xxl p-xxl text-center'}>
                    <FontAwesomeIcon icon={faTruckFast} size={"3x"} />
                    <h6 className={'sub-heading mt-sm mb-xs'}>{t("shippingCalculator.noData.title")}</h6>
                    <p>{t("shippingCalculator.noData.description")}</p>
                </StackVertical>
            )}

            {groupData && Object.keys(groupData).length > 0 && (
                <>
                    <RenderGroup {...groupData} />

                    <ol>
                        <li>All shipping rates are shown in Great Britain Pounds (GBP).</li>
                        <li>These prices include UK VAT @ 20%, but if you’re outside of the UK and operate as an Incorporated Business or Registered Charity, you won’t be charged VAT.</li>
                        <li>Our Shipping Calculator provides an estimate of the shipping total cost.</li>
                        <li>All shipping cost quoted are DDU (Delivery Duty Unpaid), unless explicitly stated. You may be liable for VAT/Duty on delivery of your items/goods in the recipients destination country.</li>
                        <li>We will calculate the final shipping total cost when forwarding your items based on the actual weight and dimensions of your package.</li>
                        <li>All services may be subject to the following additional charges depending on your item specifics and destination, including but not limited to:
                            <ol className={'roman-numeral-list'}>
                                <li>Elevated Risk & Restricted Destination Surcharge</li>
                                <li>Emergency Situation Surcharge</li>
                                <li>Export Validation</li>
                                <li>Parcel Handling / Oversized Piece</li>
                                <li>Air Carrier Supplement Charges</li>
                            </ol>
                        </li>
                    </ol>
                </>
            )}

        </>
    )

}
